import { useQuery } from '@tanstack/react-query';

import { DetailSheet } from '@/components/composed/DetailSheet';
import { MoneyCell } from '@/components/composed/MoneyCell';
import { DateCell } from '@/components/composed/DateCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { invoiceDetailQuery } from '../api/invoice-queries';
import { invoiceStatusMeta } from '../invoice-status';
import type { InvoiceId } from '@/lib/api/ids';
import { InvoiceTimeline } from './InvoiceTimeline';

/*
 * Read-only detail panel (spec §7.2, §7.6). Deep-linkable: the route controls
 * open state from search params. Preserves table context behind it.
 */
export function InvoiceDetailSheet({
  invoiceId,
  open,
  onOpenChange,
}: {
  invoiceId: InvoiceId | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <DetailSheet open={open} onOpenChange={onOpenChange} title="Invoice detail">
      {invoiceId ? <DetailBody invoiceId={invoiceId} /> : null}
    </DetailSheet>
  );
}

function DetailBody({ invoiceId }: { invoiceId: InvoiceId }) {
  const query = useQuery(invoiceDetailQuery(invoiceId));

  if (query.isLoading) {
    return <Skeleton className="h-40 w-full" />;
  }
  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">Failed to load invoice.</p>;
  }

  const invoice = query.data;
  const meta = invoiceStatusMeta(invoice.status);

  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Invoice #">{invoice.number}</Field>
        <Field label="Status">
          <StatusBadge label={meta.label} tone={meta.tone} />
        </Field>
        <Field label="Client">{invoice.clientName}</Field>
        <Field label="Amount">
          <MoneyCell
            amountMinor={invoice.amountMinor}
            currency={invoice.currency}
          />
        </Field>
        <Field label="Issued">
          <DateCell value={invoice.issuedAt} />
        </Field>
        <Field label="Due">
          <DateCell value={invoice.dueAt} variant="calendar-day" />
        </Field>
      </dl>
      <Separator />
      <div>
        <h3 className="mb-3 text-sm font-medium">Activity</h3>
        <InvoiceTimeline invoiceId={invoiceId} />
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}
